import React from 'react';
import { RefreshCw, Settings2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import PayrollDocSettingsFields from '@/components/payroll/PayrollDocSettingsFields';
import type { HeadingFlat } from '@/document-entry/headingCatalog';
import {
  buildPayrollAccrualDocumentRows,
  type PayrollDocumentEntryRow,
} from '@/lib/payroll/buildPayrollAccrualDocument';
import type { PayrollDraft } from '@/lib/payroll/payrollDraftTypes';
import {
  readPayrollDocSettings,
  savePayrollDocSettings,
  type PayrollDocSettings,
} from '@/lib/payroll/payrollDocSettings';
import { formatRial } from '@/lib/formatMoney';

type PayrollDocumentImportPanelProps = {
  draft: PayrollDraft;
  headingCatalog: HeadingFlat[];
  onApplyRows: (rows: PayrollDocumentEntryRow[], operationDescription: string) => void;
};

const PayrollDocumentImportPanel: React.FC<PayrollDocumentImportPanelProps> = ({
  draft,
  headingCatalog,
  onApplyRows,
}) => {
  const [settings, setSettings] = React.useState<PayrollDocSettings>(() => readPayrollDocSettings());
  const [showSettings, setShowSettings] = React.useState(false);

  const persistSettings = (patch: Partial<PayrollDocSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      savePayrollDocSettings(next);
      return next;
    });
  };

  const apply = () => {
    const rows = buildPayrollAccrualDocumentRows(draft, settings);
    const op = `استقرار حقوق و دستمزد — ${draft.month_label || draft.month}${draft.date_range_label ? ` (${draft.date_range_label})` : ''}`;
    onApplyRows(rows, op);
  };

  const creditTotal = draft.rows.reduce((s, r) => s + (r.net_salary || 0), 0);
  const debitTotal = (draft.project_rows || []).reduce((s, r) => s + (r.net_salary || 0), 0);

  return (
    <Card className="border-indigo-300 bg-indigo-50/40">
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitle className="text-base">سند استقرار حقوق</CardTitle>
          <CardDescription>
            حساب‌ها از محاسبه حقوق تنظیم شده‌اند. در صورت نیاز می‌توانید اینجا اصلاح و دوباره بارگذاری کنید.
          </CardDescription>
        </div>
        <Button type="button" variant="outline" size="sm" className="shrink-0 gap-1" onClick={() => setShowSettings((s) => !s)}>
          <Settings2 className="h-4 w-4" />
          {showSettings ? 'بستن تنظیمات' : 'تغییر حساب‌ها'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {!showSettings ? (
          <div className="grid gap-2 text-sm sm:grid-cols-2">
            <div className="rounded-lg border border-white bg-white/80 px-3 py-2">
              <span className="text-xs text-slate-500">بستانکار — بدهی حقوق</span>
              <div className="font-mono font-medium text-slate-800" dir="ltr">
                {settings.payable_account_code || '—'}
              </div>
            </div>
            <div className="rounded-lg border border-white bg-white/80 px-3 py-2">
              <span className="text-xs text-slate-500">بدهکار — هزینه</span>
              <div className="font-mono font-medium text-slate-800" dir="ltr">
                {settings.expense_account_code || '—'}
              </div>
            </div>
          </div>
        ) : (
          <PayrollDocSettingsFields
            settings={settings}
            onChange={persistSettings}
            headingCatalog={headingCatalog}
          />
        )}

        <div className="flex flex-wrap gap-3 text-sm text-slate-700">
          <span className="rounded-lg bg-white px-3 py-1">{draft.rows.length} بستانکار (پرسنل)</span>
          <span className="rounded-lg bg-white px-3 py-1">
            {new Set((draft.project_rows || []).map((p) => p.project_id)).size} بدهکار (پروژه)
          </span>
          <span className="rounded-lg bg-white px-3 py-1 font-mono">جمع: {formatRial(draft.total_net)}</span>
          <span className="rounded-lg bg-white px-3 py-1 font-mono">
            بستانکار: {formatRial(creditTotal)} · بدهکار: {formatRial(debitTotal)}
          </span>
        </div>

        <Button type="button" className="gap-2" onClick={apply}>
          <RefreshCw className="h-4 w-4" />
          بارگذاری مجدد ردیف‌های سند
        </Button>
      </CardContent>
    </Card>
  );
};

export default PayrollDocumentImportPanel;
